import { get, merge, has, isFunction } from 'lodash';
import { BaseClient as InfraBaseClient, sessionManager } from 'vue-infra';

import BaseClient from '../common/BaseClient';
import ApiResources from '../data/apiResources';
import { SOURCE_APP_NAME,
  WIDGET_DOMAINS,
  WIDGET_CONFIG_DOMAIN,
  WIDGET_VIEW_ID,
  WIDGET_LAYOUT_ID,
  WIDGET_TOS_ID } from '../common/consts';

const RESOURCES = Object.freeze({
  WIDGET_CONNECTION: 'WidgetConnection',
  WIDGET_VIEW: 'WidgetView',
  WIDGET_LAYOUT: 'WidgetLayout',
  GET_WIDGET_TOS: 'GetWidgetToS',
  POST_WIDGET_TOS: 'PostWidgetToS',
});

const RESOURCES_CONFIGURATION = Object.freeze({
  [RESOURCES.WIDGET_CONNECTION]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET, BaseClient.HTTP_METHODS.PUT, BaseClient.HTTP_METHODS.POST, BaseClient.HTTP_METHODS.PATCH],
    URL_KEY: () => 'WIDGET.Connection',
  },
  [RESOURCES.WIDGET_VIEW]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET, BaseClient.HTTP_METHODS.PUT, BaseClient.HTTP_METHODS.POST, BaseClient.HTTP_METHODS.PATCH],
    URL_KEY: () => 'WIDGET.View',
  },
  [RESOURCES.WIDGET_LAYOUT]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET, BaseClient.HTTP_METHODS.PUT, BaseClient.HTTP_METHODS.POST, BaseClient.HTTP_METHODS.PATCH],
    URL_KEY: () => 'WIDGET.Layout',
  },
  [RESOURCES.GET_WIDGET_TOS]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'WIDGET.TermsOfService',
  },
  [RESOURCES.POST_WIDGET_TOS]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.POST],
    URL_KEY: () => 'WIDGET.UpdateTermsOfService',
  },
});

class WidgetClient extends BaseClient {
  constructor({
    name = 'WidgetClient',
    client = null,
    resource,
    contextName,
    ...options
  }) {
    const infraBaseClient = client || createClient(options, resource, contextName);
    super({
      name,
      client: infraBaseClient,
      ...options,
    });
  }
}

function createClient(options, resource, contextName, setFurtherDomains = null, resourcesConfig = null) {
  // TODO: need to implement out own urlResolver
  const urlResolver = window.LE.urlResolver;
  setDomains(urlResolver, options);
  if (isFunction(setFurtherDomains)) {
    setFurtherDomains(urlResolver, options);
  }

  const resources = resourcesConfig || RESOURCES_CONFIGURATION;

  const objFromApiResources = get(ApiResources, resources[resource].URL_KEY());

  const baseURL = urlResolver.getUrl(objFromApiResources);
  const url = urlResolver.getUrlParts(baseURL);
  const path = `${url.pathname}${url.search}${url.hash}`;
  const params = objFromApiResources ? objFromApiResources.params : {};

  const widgetBaseClientOptions = {
    allowedMethods: resources[resource].ALLOWED_METHODS,
    path,
    source: {
      appName: SOURCE_APP_NAME,
      contextName,
    },
    config: {
      timeout: resources[resource].TIMEOUT,
      baseURL,
      params,
    },
  };
  merge(widgetBaseClientOptions, options);
  const client = new InfraBaseClient(widgetBaseClientOptions);
  return client;
}

function setDomains(urlResolver, options) {
  let accountZone = sessionManager.getAccountZone();
  if (accountZone === 'qa' || !has(WIDGET_DOMAINS, accountZone)) {
    accountZone = 'z0';
  }
  const viewId = options.viewId || 'unknown';
  const layoutId = options.layoutId || 'unknown';
  const tosId = options.tosId || 'unknown';
  const widgetConfigDomain = WIDGET_DOMAINS[accountZone][WIDGET_CONFIG_DOMAIN];
  urlResolver.addConfigValue(WIDGET_CONFIG_DOMAIN, widgetConfigDomain);
  urlResolver.addConfigValue(WIDGET_VIEW_ID, viewId);
  urlResolver.addConfigValue(WIDGET_LAYOUT_ID, layoutId);
  urlResolver.addConfigValue(WIDGET_TOS_ID, tosId);
}

WidgetClient.RESOURCES = RESOURCES;
WidgetClient.RESOURCES_CONFIGURATION = RESOURCES_CONFIGURATION;

export {
  createClient,
  RESOURCES,
  RESOURCES_CONFIGURATION,
};

export default WidgetClient;
